import styled from '@emotion/styled'
import { Stack } from '@mui/material'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import {
  useAssignBureau,
  useBureau,
  useBureauClients,
  useConfigureBureau,
  useResetBureau,
  useSaveSubordinatesPlans,
  useSearch,
  useUnassignBureau,
} from 'api/react-query'
import { BureauAgent, BureauType } from 'api/swagger/definitions/backoffice'
import FeatherIcon from 'feather-icons-react'
import Colors from 'figma/panda/Colors'
import { EnumToOptions, prettifyEnumLabel } from 'helpers/CreditOnCardHelpers'
import useDebounce from 'helpers/useDebounce'
import useMaterialNotification from 'hooks/useMaterialNotification'
import { When } from 'mynt-components/components/When'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { ApplyBureauSubscriptionPlans } from './CustomerBureauApplySubscriptionPlansButton'
import { SelectController, TextFieldController } from './react-hook-components'

const ExtendedBureauType = {
  ...BureauType,
  NONE: 'none' as const,
}

const FilteredBureauType = {
  ACCOUNTING_AGENCY: ExtendedBureauType.ACCOUNTING_AGENCY,
  NONE: 'none' as const,
}

type ExtendedBureauTypeEnum = (typeof ExtendedBureauType)[keyof typeof ExtendedBureauType]

type FormValues = {
  bureauType: ExtendedBureauTypeEnum
  bureauAgent: BureauAgent
  query: string
}

export const CustomerBureauPanel = ({ customerId }: { customerId: string }) => {
  const { mutateAsync: configureBureau, isLoading: isConfigureLoading } = useConfigureBureau()
  const { mutateAsync: resetBureau, isLoading: isResetLoading } = useResetBureau()
  const { mutateAsync: assingClientToBureau, isLoading: isAssignLoading } = useAssignBureau()
  const { mutateAsync: unAssignClientFromBureau, isLoading: isUnassignLoading } = useUnassignBureau()
  const { mutateAsync: saveSubordinatesPlans, isLoading: isSavingSubordinatesPlans } = useSaveSubordinatesPlans(customerId)

  const { data: bureauClients, isLoading: isLoadingClients, isFetching: isFetchingClients } = useBureauClients(customerId)
  const { data: bureau, isLoading: isLoadingBureau, isFetching: isFetchingBureaus } = useBureau(customerId)

  const isLoading = [
    isConfigureLoading,
    isResetLoading,
    isAssignLoading,
    isUnassignLoading,
    isLoadingBureau,
    isFetchingBureaus,
    isLoadingClients,
    isFetchingClients,
    isSavingSubordinatesPlans,
  ].some(Boolean)

  const [query, setQuery] = useState<string>('')
  const [loadingClientId, setLoadingClientId] = useState<string | null>(null)

  const notify = useMaterialNotification()

  const form = useForm<FormValues>({
    defaultValues: {
      bureauType: ExtendedBureauType.NONE,
      bureauAgent: BureauAgent.NO_AGENT,
      query: '',
    },
  })

  const formQuery = form.watch('query')

  const setQueryDebounced = useDebounce(setQuery, 200)

  useEffect(() => {
    setQueryDebounced(formQuery)
  }, [formQuery, setQueryDebounced])

  const search = useSearch(query, 200, 'CUSTOMER')

  useEffect(() => {
    if (!bureau) return

    const bureauType = bureau.type ?? ExtendedBureauType.NONE
    const bureauAgent = bureau.agent ?? BureauAgent.NO_AGENT

    form.reset({ bureauType, bureauAgent })
  }, [bureau, form.reset])

  const handleIsBureauChange = ({ bureauType }: FormValues) => {
    if (!bureauType) return

    if (bureauType === ExtendedBureauType.NONE) {
      return resetBureau({ bureauId: customerId }).then(() => {
        notify('Customer is no longer a bureau')
      })
    }

    configureBureau({ bureauId: customerId, type: bureauType })
      .then(() => {
        notify('Customer is now a bureau')
      })
      .catch(() => {
        form.setValue('bureauType', ExtendedBureauType.NONE)
      })
  }

  const handleSaveSubordinatesPlans = () => {
    saveSubordinatesPlans().then(() => {
      notify('Subscription plans applied to subordinates')
    })
  }

  const handleBureauAgent = ({ bureauAgent, bureauType }: FormValues) => {
    if (!bureauAgent || bureauType === ExtendedBureauType.NONE) return

    configureBureau({
      bureauId: customerId,
      agent: bureauAgent,
      type: bureauType,
    })
      .then(() => {
        notify(`Customer is now a ${prettifyEnumLabel(bureauAgent)} agent`)
      })
      .catch(() => {
        form.setValue('bureauType', ExtendedBureauType.NONE)
      })
  }

  const handleAddBureauClient = ({ bureauId, clientId }: { bureauId: string; clientId: string }) => {
    setLoadingClientId(clientId)

    assingClientToBureau({ bureauId, bureauClientId: clientId })
      .then(() => {
        notify('Client added to bureau')
      })
      .finally(() => {
        setLoadingClientId(null)
      })
  }

  const handleUnassign = (clientId: string | undefined) => {
    unAssignClientFromBureau({ bureauClientId: clientId })
  }

  return (
    <Box>
      <Stack direction="row" gap={2} alignItems="center" justifyContent="space-between" sx={{ width: '100%', paddingBottom: '24px' }}>
        <Stack direction="row" gap={1} alignItems="center">
          <Typography variant="h2">Bureau</Typography>
          {isLoading && <CircularProgress size="24px" />}
        </Stack>
        <ApplyBureauSubscriptionPlans onSave={handleSaveSubordinatesPlans} />
      </Stack>
      <Box sx={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
        <Box
          sx={{
            display: 'flex',
            gap: '16px',
            flexDirection: 'column',
            minWidth: '300px',
            flex: 1,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '32px', flex: 1 }}>
            <SelectController
              control={form.control}
              name="bureauType"
              labelText="Bureau type"
              onChange={form.handleSubmit(handleIsBureauChange)}
              options={[...EnumToOptions(FilteredBureauType, prettifyEnumLabel)]}
              disabled={isLoading}
            />
            <SelectController
              control={form.control}
              name="bureauAgent"
              disabled={form.watch('bureauType') !== ExtendedBureauType.ACCOUNTING_AGENCY || isLoading}
              labelText="Bureau agent"
              onChange={form.handleSubmit(handleBureauAgent)}
              options={[...EnumToOptions(BureauAgent, prettifyEnumLabel)]}
            />
          </Box>
          <When is={bureauClients && bureauClients.length > 0}>
            <Box>
              Clients:
              <Box sx={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
                {bureauClients
                  ?.sort((a, b) => {
                    const nameA = a.bureauClientName as string
                    const nameB = b.bureauClientName as string

                    return nameA?.localeCompare(nameB)
                  })
                  .map((bureau) => (
                    <Typography key={bureau.bureauClientId}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '4px',
                          alignItems: 'center',
                        }}
                      >
                        <a
                          style={{ color: Colors.brandBlueCTA }}
                          target="_blank"
                          href={`/customers/${bureau.bureauClientId}`}
                          rel="noreferrer"
                        >
                          {bureau.bureauClientName}
                        </a>
                        {bureau.bureauClientName && (
                          <IconButton onClick={() => handleUnassign(bureau.bureauClientId)} size="small">
                            <ColoredFeatherIcon color={Colors.notificationErrors500} size={16} strokeWidth={4} icon="minus" />
                          </IconButton>
                        )}
                      </Box>
                    </Typography>
                  ))}
              </Box>
            </Box>
          </When>
        </Box>
        <Box sx={{ display: 'flex', gap: '8px', flexDirection: 'column', flex: 1 }}>
          <Box>
            <TextFieldController
              disabled={form.watch('bureauType') === ExtendedBureauType.NONE || isLoading}
              control={form.control}
              name="query"
              labelText="Search customers to add as bureau client"
            />
          </Box>
          <Box
            sx={{
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            {search.data?.items
              .filter((item) => item.subTitle.endsWith('MYNT'))
              .map((item) => {
                const isAlreadyClient = bureauClients?.some((client) => client.bureauClientId === item.customerId)
                const isSelf = customerId === item.customerId

                return (
                  <Box
                    key={item.id}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '8px',
                      height: '36px',
                    }}
                  >
                    <Typography>{item.title}</Typography>
                    <When is={isAlreadyClient}>
                      <ColoredFeatherIcon color={Colors.success700} icon="check" />
                    </When>
                    <When is={isSelf}>🫵</When>
                    <When is={!isAlreadyClient}>
                      <When is={customerId !== item.customerId}>
                        <IconButton
                          disabled={isLoading}
                          onClick={() =>
                            handleAddBureauClient({
                              bureauId: customerId,
                              clientId: item.customerId,
                            })
                          }
                          size="small"
                        >
                          {loadingClientId === item.customerId ? <CircularProgress size="24px" /> : <FeatherIcon size={24} icon="plus" />}
                        </IconButton>
                      </When>
                    </When>
                  </Box>
                )
              })}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const ColoredFeatherIcon = styled(FeatherIcon)<{ color: string }>`
  stroke: ${(props) => props.color};
`
